import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import api from '../utils/api';
import Footer from '../components/Footer';
import { getImageUrl } from '../utils/imageUtils';
import SimpleLoader from '../components/SimpleLoader';
import EditListing from '../components/EditListing';

const Dashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('listings');
  const [listings, setListings] = useState([]);
  const [userStats, setUserStats] = useState(null);
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRentalModal, setShowRentalModal] = useState(false);
  const [showSaleModal, setShowSaleModal] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);
  const [rentalForm, setRentalForm] = useState({
    renterName: '',
    renterContact: '',
    renterLocation: '',
    expectedReturnDate: '',
    notes: ''
  });
  const [saleForm, setSaleForm] = useState({
    buyerName: '',
    buyerContact: '',
    buyerLocation: '',
    notes: ''
  });
  const [showReturnConfirm, setShowReturnConfirm] = useState(false);
  const [itemToReturn, setItemToReturn] = useState(null);
  const [returnNotes, setReturnNotes] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [itemToEdit, setItemToEdit] = useState(null);

  useEffect(() => {
    loadUserStats();
    loadListings();
  }, []);

  const loadUserStats = async () => {
    try {
      const response = await api.get('/listings/check-limit');
      setUserStats(response.data);
    } catch (error) {
      // Fallback to membership status
      try {
        const fallbackResponse = await api.get('/membership/status');
        setUserStats(fallbackResponse.data);
      } catch (fallbackError) {
      }
    }
  };

  const loadListings = async () => {
    try {
      const response = await api.get('/listings/user/my-listings');

      if (response.data && response.data.listings) {
        setListings(response.data.listings);
      } else {
        setListings([]); // Fallback to empty array
      }
    } catch (error) {
      setListings([]); // Ensure listings is always an array
    } finally {
      setLoading(false);
    }
  };

  const loadChats = async () => {
    try {
      const response = await api.get('/chat/my-chats');

      // Filter out any invalid chats with comprehensive null checks
      const validChats = (response.data.chats || []).filter(chat => {
        // Check if chat has required properties
        if (!chat || !chat._id) return false;
        
        // Check if buyer and seller exist and have IDs
        const buyer = chat.buyer || {};
        const seller = chat.seller || {};
        
        if (!buyer._id || !seller._id) return false;
        
        // Filter out chats where user might be chatting with themselves
        if (buyer._id === seller._id) return false;
        
        return true;
      });
      
      setChats(validChats);
    } catch (error) {
      setChats([]); // Ensure chats is always an array
    }
  };



  const switchTab = (tab) => {
    setActiveTab(tab);
    if (tab === 'chats' && chats.length === 0) {
      loadChats();
    }
  };

  const getStatusBadge = (listing) => {
    if (new Date(listing.expiresAt) <= new Date()) {
      return <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs">Expired</span>;
    } else if (listing.isActive) {
      return <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">Active</span>;
    } else {
      return <span className="px-2 py-1 rounded text-xs" style={{ backgroundColor: '#948979', color: 'white', opacity: 0.8 }}>Inactive</span>;
    }
  };

  const getExpiryInfo = (listing) => {
    const expiry = new Date(listing.expiresAt);
    const now = new Date();
    const daysLeft = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));

    if (daysLeft <= 0) {
      return <p className="text-xs text-red-600">Expired</p>;
    } else if (daysLeft <= 3) {
      return <p className="text-xs text-orange-600">{daysLeft} days left</p>;
    } else {
      return <p className="text-xs" style={{ color: '#393E46' }}>{daysLeft} days left</p>;
    }
  };



  const handleMarkAsRented = (listingId) => {
    const listing = listings.find(l => l._id === listingId);
    setSelectedListing(listing);
    setShowRentalModal(true);
  };

  const handleMarkAsSold = (listingId) => {
    const listing = listings.find(l => l._id === listingId);
    setSelectedListing(listing);
    setShowSaleModal(true);
  };

  const submitSale = async () => {
    // Validate required fields
    if (!saleForm.buyerName.trim()) {
      toast.error('Please enter the buyer\'s name');
      return;
    }

    if (saleForm.buyerName.trim().length < 2) {
      toast.error('Buyer name must be at least 2 characters long');
      return;
    }

    if (!/^[a-zA-Z\s]+$/.test(saleForm.buyerName.trim())) {
      toast.error('Buyer name can only contain letters and spaces');
      return;
    }

    if (!saleForm.buyerContact.trim()) {
      toast.error('Please enter the buyer\'s contact number');
      return;
    }

    if (!/^\d{10}$/.test(saleForm.buyerContact.trim())) {
      toast.error('Contact number must be exactly 10 digits');
      return;
    }

    try {
      const response = await api.post(`/listings/${selectedListing._id}/sell`, {
        buyerName: saleForm.buyerName.trim(),
        buyerContact: saleForm.buyerContact.trim(),
        buyerLocation: saleForm.buyerLocation.trim() || null,
        notes: saleForm.notes.trim() || null
      });

      if (response.data) {
        // Refresh listings to show updated status
        loadListings();
        toast.success(`Item sold for ₹${selectedListing.price}!`);
        setShowSaleModal(false);
        setSaleForm({ buyerName: '', buyerContact: '', buyerLocation: '', notes: '' });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error marking item as sold');
    }
  };

  const submitRental = async () => {
    // Validate required fields
    if (!rentalForm.renterName.trim()) {
      toast.error('Please enter the renter\'s name');
      return;
    }

    if (rentalForm.renterName.trim().length < 2) {
      toast.error('Renter name must be at least 2 characters long');
      return;
    }

    if (!/^[a-zA-Z\s]+$/.test(rentalForm.renterName.trim())) {
      toast.error('Renter name can only contain letters and spaces');
      return;
    }

    if (!rentalForm.renterContact.trim()) {
      toast.error('Please enter the renter\'s contact number');
      return;
    }

    if (!/^\d{10}$/.test(rentalForm.renterContact.trim())) {
      toast.error('Contact number must be exactly 10 digits');
      return;
    }

    if (!rentalForm.expectedReturnDate) {
      toast.error('Please select an expected return date');
      return;
    }

    const returnDate = new Date(rentalForm.expectedReturnDate);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);

    // Set time to start of day for comparison
    today.setHours(0, 0, 0, 0);
    tomorrow.setHours(0, 0, 0, 0);
    returnDate.setHours(0, 0, 0, 0);

    if (returnDate < tomorrow) {
      toast.error('Return date must be at least tomorrow');
      return;
    }

    // Check if return date is too far in the future (max 30 days)
    const maxReturnDate = new Date(today);
    maxReturnDate.setDate(today.getDate() + 30);

    if (returnDate > maxReturnDate) {
      toast.error('Return date cannot be more than 30 days from today');
      return;
    }

    try {
      const response = await api.post(`/listings/${selectedListing._id}/rent`, {
        renterName: rentalForm.renterName.trim(),
        renterContact: rentalForm.renterContact.trim(),
        renterLocation: rentalForm.renterLocation.trim() || null,
        expectedReturnDate: rentalForm.expectedReturnDate,
        notes: rentalForm.notes.trim() || null
      });

      if (response.data) {
        // Refresh listings to show updated status
        loadListings();
        const rentalInfo = response.data.rentalInfo;
        toast.success(`Item rented for ${rentalInfo.totalDays} days. Total: ₹${rentalInfo.totalAmount}`);
        setShowRentalModal(false);
        setRentalForm({ renterName: '', renterContact: '', renterLocation: '', expectedReturnDate: '', notes: '' });
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error marking item as rented');
    }
  };

  const handleMarkAsReturned = (listingId) => {
    const listing = listings.find(l => l._id === listingId);
    setItemToReturn(listing);
    setShowReturnConfirm(true);
  };

  const confirmReturn = async () => {
    try {
      const response = await api.post(`/listings/${itemToReturn._id}/return`, {
        returnNotes: returnNotes.trim()
      });

      if (response.data) {
        // Refresh listings to show updated status
        loadListings();
        toast.success('Item marked as returned successfully!');
        setShowReturnConfirm(false);
        setItemToReturn(null);
        setReturnNotes('');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error marking item as returned');
    }
  };

  const handleDeleteListing = (listingId) => {
    const listing = listings.find(l => l._id === listingId);
    setItemToDelete(listing);
    setShowDeleteConfirm(true);
  };

  const handleEditListing = (listingId) => {
    const listing = listings.find(l => l._id === listingId);
    setItemToEdit(listing);
    setShowEditModal(true);
  };

  const handleEditClose = () => {
    setShowEditModal(false);
    setItemToEdit(null);
  };

  const handleEditUpdate = (updatedListing) => {
    // Update the listing in the local state
    setListings(prevListings => 
      prevListings.map(listing => 
        listing._id === updatedListing._id ? updatedListing : listing
      )
    );
  };

  const confirmDelete = async () => {
    try {
      const response = await api.delete(`/listings/${itemToDelete._id}`);

      if (response.data) {
        // Refresh listings to show updated list
        loadListings();
        // Refresh user stats to update deleted listings counter
        loadUserStats();
        toast.success('Listing deleted successfully!');
        setShowDeleteConfirm(false);
        setItemToDelete(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error deleting listing');
    }
  };

  const getStats = () => {
    // Safety check to ensure listings is always an array
    const safeListings = Array.isArray(listings) ? listings : [];

    const total = safeListings.length;
    const active = safeListings.filter(l => l.isActive && new Date(l.expiresAt) > new Date()).length;
    const expired = safeListings.filter(l => new Date(l.expiresAt) <= new Date()).length;
    const rented = safeListings.filter(l => l.isRented).length;
    const sold = safeListings.filter(l => l.isSold).length;
    const available = safeListings.filter(l => {
      // Item is available if:
      // 1. Not sold AND
      // 2. Either not rented OR rental has expired
      if (l.isSold) return false;
      if (!l.isRented) return true;
      
      // Check if rental has expired
      if (l.rentalDetails?.expectedReturnDate) {
        const returnDate = new Date(l.rentalDetails.expectedReturnDate);
        const today = new Date();
        return returnDate < today; // Available if rental expired
      }
      
      return false; // Rented with no return date = not available
    }).filter(l => l.isActive && new Date(l.expiresAt) > new Date()).length;

    return { total, active, expired, rented, sold, available };
  };

  // Filter listings based on tab
  const getFilteredListings = () => {
    // Safety check to ensure listings is always an array
    const safeListings = Array.isArray(listings) ? listings : [];

    if (activeTab === 'listings') {
      // Show only non-rented and non-sold items in My Listings
      return safeListings.filter(l => !l.isRented && !l.isSold);
    } else if (activeTab === 'rented') {
      // Show only rented items in Rented Items
      return safeListings.filter(l => l.isRented);
    } else if (activeTab === 'sold') {
      // Show only sold items in Sold Items
      return safeListings.filter(l => l.isSold);
    }
    return safeListings;
  };

  const stats = getStats();

  return (
    <>
      <Helmet>
        <title>Dashboard - VestrioZ</title>
        <meta
          name="description"
          content="Manage your listings, rentals, and account on VestrioZ."
        />
        <meta name="keywords" content="dashboard, my listings, manage items, VestrioZ" />
      </Helmet>
      <div className="min-h-screen" style={{ backgroundColor: '#F6F1EB' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-8">
          {/* Header */}
          <div className="text-center mb-8 md:mb-12">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4" style={{ color: '#222831' }}>
              My Dashboard
            </h1>
            <p className="text-base md:text-lg max-w-2xl mx-auto px-4" style={{ color: '#393E46' }}>
              Manage your listings, track rentals, and grow your rental business
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-6 mb-8 md:mb-12">
            <div className="rounded-2xl shadow-xl p-6 border card-hover" style={{ backgroundColor: 'white', borderColor: '#948979' }}>
              <div className="flex items-center">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#222831' }}>
                  <i className="fas fa-list text-white text-xl"></i>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium" style={{ color: '#393E46' }}>Total Listings</p>
                  <p className="text-3xl font-bold" style={{ color: '#222831' }}>{stats.total}</p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl shadow-xl p-6 border card-hover" style={{ backgroundColor: 'white', borderColor: '#948979' }}>
              <div className="flex items-center">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#948979' }}>
                  <i className="fas fa-check-circle text-white text-xl"></i>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium" style={{ color: '#393E46' }}>Available Items</p>
                  <p className="text-3xl font-bold" style={{ color: '#222831' }}>{stats.available}</p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl shadow-xl p-6 border card-hover" style={{ backgroundColor: 'white', borderColor: '#948979' }}>
              <div className="flex items-center">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#393E46' }}>
                  <i className="fas fa-clock text-white text-xl"></i>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium" style={{ color: '#393E46' }}>Expired Listings</p>
                  <p className="text-3xl font-bold" style={{ color: '#222831' }}>{stats.expired}</p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl shadow-xl p-6 border card-hover" style={{ backgroundColor: 'white', borderColor: '#948979' }}>
              <div className="flex items-center">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#948979' }}>
                  <i className="fas fa-handshake text-white text-xl"></i>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium" style={{ color: '#393E46' }}>Currently Rented</p>
                  <p className="text-3xl font-bold" style={{ color: '#222831' }}>{stats.rented}</p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl shadow-xl p-6 border card-hover" style={{ backgroundColor: 'white', borderColor: '#948979' }}>
              <div className="flex items-center">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#393E46' }}>
                  <i className="fas fa-tag text-white text-xl"></i>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium" style={{ color: '#393E46' }}>Items Sold</p>
                  <p className="text-3xl font-bold" style={{ color: '#222831' }}>{stats.sold}</p>
                </div>
              </div>
            </div>

            <div className="rounded-2xl shadow-xl p-6 border card-hover" style={{ backgroundColor: 'white', borderColor: '#948979' }}>
              <div className="flex items-center">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#393E46' }}>
                  <i className="fas fa-trash text-white text-xl"></i>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium" style={{ color: '#393E46' }}>Deleted Listings</p>
                  <p className="text-3xl font-bold" style={{ color: '#222831' }}>{userStats?.deletedListings || 0}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Membership Info */}
          <div className={`text-white rounded-lg p-4 md:p-6 mb-6 md:mb-8`} style={{
            backgroundColor: userStats?.canCreateListing ? '#393E46' : '#393E46'
          }}>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div className="flex-1">
                <h3 className="text-lg md:text-xl font-bold mb-2 flex items-center">
                  <i className={`mr-2 ${userStats?.canCreateListing ? 'fas fa-check-circle' : 'fas fa-exclamation-triangle'
                    }`}></i>
                  Listing Status
                </h3>
                <div className="space-y-1 text-sm md:text-base">
                  <p>Free Listings: {userStats?.freeListingsUsed || 0}/{userStats?.freeListingsLimit || 3}</p>
                  <p>Membership Listings: {userStats?.membershipListingsRemaining || 0} remaining</p>
                  <p>Deleted Listings: {userStats?.deletedListings || 0} total</p>
                  <p className="font-medium mt-2">
                    {userStats?.message || 'Loading...'}
                  </p>
                </div>
              </div>
              <div className="w-full md:w-auto">
                {userStats?.canCreateListing ? (
                  <Link
                    to="/membership"
                    className="bg-white/20 backdrop-blur-sm text-white border border-white/30 px-4 md:px-6 py-2 rounded-lg font-semibold hover:bg-white/30 transition-colors text-sm md:text-base w-full md:w-auto text-center block"
                  >
                    <i className="fas fa-crown mr-2"></i>
                    Upgrade Membership
                  </Link>
                ) : (
                  <Link
                    to="/membership"
                    className="bg-white text-red-600 px-4 md:px-6 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-sm md:text-base w-full md:w-auto text-center block animate-pulse"
                  >
                    <i className="fas fa-crown mr-2"></i>
                    Upgrade Now!
                  </Link>
                )}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="rounded-2xl shadow-2xl border" style={{ backgroundColor: 'white', borderColor: '#948979' }}>
            <div className="border-b" style={{ borderColor: '#948979' }}>
              {/* Desktop Tabs */}
              <nav className="hidden md:flex space-x-8 px-6">
                <button
                  className={`py-4 px-2 border-b-2 font-medium transition-all duration-300 ${activeTab === 'listings'
                    ? 'text-white'
                    : 'border-transparent hover:text-gray-700'
                    }`}
                  style={{
                    borderColor: activeTab === 'listings' ? '#222831' : 'transparent',
                    color: activeTab === 'listings' ? '#222831' : '#393E46'
                  }}
                  onClick={() => switchTab('listings')}
                >
                  <i className="fas fa-list mr-2"></i>My Listings ({stats.available})
                </button>
                <button
                  className={`py-4 px-2 border-b-2 font-medium transition-all duration-300 ${activeTab === 'rented'
                    ? 'text-white'
                    : 'border-transparent hover:text-gray-700'
                    }`}
                  style={{
                    borderColor: activeTab === 'rented' ? '#222831' : 'transparent',
                    color: activeTab === 'rented' ? '#222831' : '#393E46'
                  }}
                  onClick={() => switchTab('rented')}
                >
                  <i className="fas fa-handshake mr-2"></i>Rented Items ({stats.rented})
                </button>
                <button
                  className={`py-4 px-2 border-b-2 font-medium transition-all duration-300 ${activeTab === 'sold'
                    ? 'text-white'
                    : 'border-transparent hover:text-gray-700'
                    }`}
                  style={{
                    borderColor: activeTab === 'sold' ? '#222831' : 'transparent',
                    color: activeTab === 'sold' ? '#222831' : '#393E46'
                  }}
                  onClick={() => switchTab('sold')}
                >
                  <i className="fas fa-tag mr-2"></i>Sold Items ({stats.sold})
                </button>
                <button
                  className={`py-4 px-2 border-b-2 font-medium transition-all duration-300 ${activeTab === 'chats'
                    ? 'text-white'
                    : 'border-transparent hover:text-gray-700'
                    }`}
                  style={{
                    borderColor: activeTab === 'chats' ? '#222831' : 'transparent',
                    color: activeTab === 'chats' ? '#222831' : '#393E46'
                  }}
                  onClick={() => switchTab('chats')}
                >
                  <i className="fas fa-comments mr-2"></i>My Chats
                </button>
              </nav>

              {/* Mobile Tabs - Grid Layout */}
              <div className="md:hidden grid grid-cols-4 gap-1 p-2">
                <button
                  className={`py-2 px-1 rounded-lg font-medium text-xs transition-all duration-300 flex flex-col items-center ${activeTab === 'listings'
                    ? 'text-white shadow-lg'
                    : ''
                    }`}
                  style={{
                    backgroundColor: activeTab === 'listings' ? '#222831' : 'transparent',
                    color: activeTab === 'listings' ? 'white' : '#393E46'
                  }}
                  onClick={() => switchTab('listings')}
                >
                  <i className="fas fa-list text-sm mb-1"></i>
                  <span>Available</span>
                  <span className="text-xs">({stats.available})</span>
                </button>
                <button
                  className={`py-2 px-1 rounded-lg font-medium text-xs transition-all duration-300 flex flex-col items-center ${activeTab === 'rented'
                    ? 'text-white shadow-lg'
                    : ''
                    }`}
                  style={{
                    backgroundColor: activeTab === 'rented' ? '#222831' : 'transparent',
                    color: activeTab === 'rented' ? 'white' : '#393E46'
                  }}
                  onClick={() => switchTab('rented')}
                >
                  <i className="fas fa-handshake text-sm mb-1"></i>
                  <span>Rented</span>
                  <span className="text-xs">({stats.rented})</span>
                </button>
                <button
                  className={`py-2 px-1 rounded-lg font-medium text-xs transition-all duration-300 flex flex-col items-center ${activeTab === 'sold'
                    ? 'text-white shadow-lg'
                    : ''
                    }`}
                  style={{
                    backgroundColor: activeTab === 'sold' ? '#222831' : 'transparent',
                    color: activeTab === 'sold' ? 'white' : '#393E46'
                  }}
                  onClick={() => switchTab('sold')}
                >
                  <i className="fas fa-tag text-sm mb-1"></i>
                  <span>Sold</span>
                  <span className="text-xs">({stats.sold})</span>
                </button>
                <button
                  className={`py-2 px-1 rounded-lg font-medium text-xs transition-all duration-300 flex flex-col items-center ${activeTab === 'chats'
                    ? 'text-white shadow-lg'
                    : ''
                    }`}
                  style={{
                    backgroundColor: activeTab === 'chats' ? '#222831' : 'transparent',
                    color: activeTab === 'chats' ? 'white' : '#393E46'
                  }}
                  onClick={() => switchTab('chats')}
                >
                  <i className="fas fa-comments text-sm mb-1"></i>
                  <span>Chats</span>
                </button>
              </div>
            </div>

            {/* Tab Content */}
            <div className="p-4 md:p-6">
              {/* Listings Tab */}
              {activeTab === 'listings' && (
                <div>
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 md:mb-6 gap-3">
                    <h3 className="text-lg font-semibold">Available Listings ({stats.available})</h3>
                    {userStats?.canCreateListing ? (
                      <Link
                        to="/list-items"
                        className="bg-indigo-600 text-white px-3 md:px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors text-sm md:text-base w-full sm:w-auto text-center"
                      >
                        <i className="fas fa-plus mr-2"></i>Add New Listing
                      </Link>
                    ) : (
                      <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                        <button
                          disabled
                          className="bg-gray-400 text-white px-3 md:px-4 py-2 rounded-lg cursor-not-allowed text-sm md:text-base w-full sm:w-auto text-center opacity-60"
                          title="You have reached your listing limit"
                        >
                          <i className="fas fa-plus mr-2"></i>Add New Listing
                        </button>
                        <Link
                          to="/membership"
                          className="text-white px-3 md:px-4 py-2 rounded-lg transition-colors text-sm md:text-base w-full sm:w-auto text-center"
                          style={{ backgroundColor: '#948979' }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#393E46'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#948979'}
                        >
                          <i className="fas fa-crown mr-2"></i>Upgrade
                        </Link>
                      </div>
                    )}
                  </div>

                  {loading ? (
                    <div className="text-center py-6 md:py-8">
                      <SimpleLoader size="lg" text="Loading listings..." />
                    </div>
                  ) : getFilteredListings().length === 0 ? (
                    <div className="text-center py-8 md:py-12 px-4">
                      <i className="fas fa-box-open text-3xl md:text-4xl mb-3 md:mb-4" style={{ color: '#948979' }}></i>
                      <h3 className="text-lg md:text-xl font-semibold mb-2" style={{ color: '#393E46' }}>No available listings</h3>
                      <p className="text-sm md:text-base mb-4" style={{ color: '#393E46', opacity: 0.8 }}>
                        {listings.length === 0 ? 'Start by listing your first item' : 'All your items are currently rented out'}
                      </p>
                      {listings.length === 0 && (
                        <Link
                          to="/list-items"
                          className="bg-indigo-600 text-white px-4 md:px-6 py-2 rounded-lg hover:bg-indigo-700 transition-colors text-sm md:text-base"
                        >
                          List Your First Item
                        </Link>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-3 md:space-y-4">
                      {getFilteredListings().map(listing => (
                        <div key={listing._id} className="border border-gray-200 rounded-lg p-3 md:p-4 bg-white shadow-sm listing-clickable">
                          {/* Mobile Layout */}
                          <div className="block md:hidden relative">

                            <div
                              className="flex items-start space-x-3 mb-3 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors"
                              onClick={() => navigate(`/listing/${listing._id}`)}
                            >
                              {listing.images && listing.images.length > 0 ? (
                                <img
                                  src={getImageUrl(listing.images[0])}
                                  alt={listing.itemName}
                                  className="w-16 h-16 object-contain rounded-lg flex-shrink-0 listing-image bg-gray-50"
                                  onError={(e) => {
                                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjY0IiBoZWlnaHQ9IjY0IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yMCAyMEg0NFY0NEgyMFYyMFoiIHN0cm9rZT0iIzlDQTNBRiIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPHA+CjwvcGF0aD4KPC9zdmc+';
                                  }}
                                />
                              ) : (
                                <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                  <i className="fas fa-image text-gray-400 text-lg"></i>
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <h4 className="font-semibold text-base truncate listing-title">{listing.itemName}</h4>
                                <p className="text-sm text-gray-600">
                                  ₹{listing.price}{(listing.listingType || 'rent') === 'rent' ? '/day' : ''}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {listing.itemType === 'jewellery' ? (listing.jewellerySize || 'Free Size') : listing.size} • {listing.location}
                                </p>
                              </div>
                              <div className="text-right flex-shrink-0">
                                <div className="text-lg font-bold text-indigo-600">₹{listing.price}</div>
                                <div className="text-xs text-gray-500 mb-1">
                                  <i className="fas fa-eye mr-1"></i>{listing.views || 0} views
                                </div>
                                <div className="mt-1">
                                  {getStatusBadge(listing)}
                                </div>
                              </div>
                            </div>

                            {/* Mobile Tags */}
                            <div className="flex flex-wrap gap-1 mb-3">
                              <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs capitalize">
                                {listing.category || 'Handycraft'}
                              </span>
                              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs capitalize">
                                {listing.itemType === 'jewellery' ? listing.jewelleryType : (listing.itemType === 'handycraft' ? listing.handycraftType : listing.clothingType)}
                              </span>
                              <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs capitalize">
                                {listing.condition}
                              </span>

                            </div>

                            {/* Mobile Description */}
                            <p className="text-xs text-gray-500 mb-3 line-clamp-2">
                              {listing.description ? `${listing.description.substring(0, 80)}...` : 'No description available'}
                            </p>



                            {/* Mobile Status Info and Action Buttons */}
                            <div className="flex justify-between items-end">
                              <div>
                                <div className="text-xs text-gray-500 mb-1">
                                  {getExpiryInfo(listing)}
                                </div>
                                <div className="text-xs text-gray-400 view-details-hint">
                                  <i className="fas fa-eye mr-1"></i>Click to view details
                                </div>
                              </div>

                              {/* Action Buttons - Bottom Right */}
                              <div className="flex space-x-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditListing(listing._id);
                                  }}
                                  className="text-white p-2 rounded-full shadow-lg transition-colors"
                                  style={{
                                    backgroundColor: '#948979',
                                    '&:hover': { backgroundColor: '#393E46' }
                                  }}
                                  onMouseEnter={(e) => e.target.style.backgroundColor = '#393E46'}
                                  onMouseLeave={(e) => e.target.style.backgroundColor = '#948979'}
                                  title="Edit listing"
                                >
                                  <i className="fas fa-edit text-xs"></i>
                                </button>
                                {(listing.listingType || 'rent') === 'rent' ? (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleMarkAsRented(listing._id);
                                    }}
                                    className="text-white p-2 rounded-full shadow-lg transition-colors"
                                    style={{
                                      backgroundColor: '#948979',
                                      '&:hover': { backgroundColor: '#393E46' }
                                    }}
                                    onMouseEnter={(e) => e.target.style.backgroundColor = '#393E46'}
                                    onMouseLeave={(e) => e.target.style.backgroundColor = '#948979'}
                                    title="Mark as Rented"
                                  >
                                    <i className="fas fa-handshake text-xs"></i>
                                  </button>
                                ) : (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleMarkAsSold(listing._id);
                                    }}
                                    className="text-white p-2 rounded-full shadow-lg transition-colors"
                                    style={{
                                      backgroundColor: '#948979',
                                      '&:hover': { backgroundColor: '#393E46' }
                                    }}
                                    onMouseEnter={(e) => e.target.style.backgroundColor = '#393E46'}
                                    onMouseLeave={(e) => e.target.style.backgroundColor = '#948979'}
                                    title="Mark as Sold"
                                  >
                                    <i className="fas fa-tag text-xs"></i>
                                  </button>
                                )}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteListing(listing._id);
                                  }}
                                  className="text-white p-2 rounded-full shadow-lg transition-colors"
                                  style={{
                                    backgroundColor: '#222831',
                                    '&:hover': { backgroundColor: '#393E46' }
                                  }}
                                  onMouseEnter={(e) => e.target.style.backgroundColor = '#393E46'}
                                  onMouseLeave={(e) => e.target.style.backgroundColor = '#222831'}
                                  title="Delete listing"
                                >
                                  <i className="fas fa-trash text-xs"></i>
                                </button>
                              </div>
                            </div>
                          </div>

                          {/* Desktop Layout */}
                          <div className="hidden md:block relative">
                            <div
                              className="flex items-start space-x-4 cursor-pointer hover:bg-gray-50 p-3 rounded-lg transition-colors"
                              onClick={() => navigate(`/listing/${listing._id}`)}
                            >
                              {listing.images && listing.images.length > 0 ? (
                                <img
                                  src={getImageUrl(listing.images[0])}
                                  alt={listing.itemName}
                                  className="w-20 h-20 object-contain rounded-lg listing-image bg-gray-50"
                                  onError={(e) => {
                                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHZpZXdCb3g9IjAgMCA4MCA4MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjgwIiBoZWlnaHQ9IjgwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yNSAyNUg1NVY1NUgyNVYyNVoiIHN0cm9rZT0iIzlDQTNBRiIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPHA+CjwvcGF0aD4KPC9zdmc+';
                                  }}
                                />
                              ) : (
                                <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center">
                                  <i className="fas fa-image text-gray-400 text-xl"></i>
                                </div>
                              )}
                              <div className="flex-1">
                                <div className="flex justify-between items-start">
                                  <div className="flex-1 pr-4">
                                    <h4 className="font-semibold text-lg listing-title">{listing.itemName}</h4>
                                    <p className="text-gray-600">
                                      ₹{listing.price}{(listing.listingType || 'rent') === 'rent' ? '/day' : ''} • {listing.itemType === 'jewellery' ? (listing.jewellerySize || 'Free Size') : listing.size} • {listing.location}
                                    </p>
                                    <p className="text-sm text-gray-500 mt-1">
                                      {listing.description ? `${listing.description.substring(0, 100)}...` : 'No description available'}
                                    </p>
                                  </div>
                                  <div className="text-right flex-shrink-0 min-w-[120px]">
                                    <span className="text-2xl font-bold text-indigo-600">₹{listing.price}</span>
                                    <div className="text-sm text-gray-500 mt-1">
                                      <i className="fas fa-eye mr-1"></i>{listing.views || 0} views
                                    </div>
                                    <div className="mt-2 space-y-1">
                                      {getStatusBadge(listing)}
                                      {getExpiryInfo(listing)}
                                    </div>
                                  </div>
                                </div>
                                <div className="mt-3 flex justify-between items-center">
                                  <div className="flex space-x-2">
                                    <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs capitalize">
                                      {listing.category || 'Handycraft'}
                                    </span>
                                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs capitalize">
                                      {listing.itemType === 'jewellery' ? listing.jewelleryType : (listing.itemType === 'handycraft' ? listing.handycraftType : listing.clothingType)}
                                    </span>
                                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs capitalize">
                                      {listing.condition}
                                    </span>
                                  </div>

                                  {/* Click to view details hint */}
                                  <div className="text-xs text-gray-400 view-details-hint">
                                    <i className="fas fa-eye mr-1"></i>Click to view details
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            {/* Action Buttons - Bottom Right, outside clickable area */}
                            <div className="flex justify-end space-x-2 mt-3 px-3">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditListing(listing._id);
                                }}
                                className="text-white px-3 py-1.5 rounded-lg shadow-md transition-colors text-sm"
                                style={{ backgroundColor: '#948979' }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#393E46'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = '#948979'}
                                title="Edit listing"
                              >
                                <i className="fas fa-edit mr-1"></i>
                                Edit
                              </button>
                              {(listing.listingType || 'rent') === 'rent' ? (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMarkAsRented(listing._id);
                                  }}
                                  className="text-white px-3 py-1.5 rounded-lg shadow-md transition-colors text-sm"
                                  style={{ backgroundColor: '#948979' }}
                                  onMouseEnter={(e) => e.target.style.backgroundColor = '#393E46'}
                                  onMouseLeave={(e) => e.target.style.backgroundColor = '#948979'}
                                  title="Mark as Rented"
                                >
                                  <i className="fas fa-handshake mr-1"></i>
                                  Rent
                                </button>
                              ) : (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMarkAsSold(listing._id);
                                  }}
                                  className="text-white px-3 py-1.5 rounded-lg shadow-md transition-colors text-sm"
                                  style={{ backgroundColor: '#948979' }}
                                  onMouseEnter={(e) => e.target.style.backgroundColor = '#393E46'}
                                  onMouseLeave={(e) => e.target.style.backgroundColor = '#948979'}
                                  title="Mark as Sold"
                                >
                                  <i className="fas fa-tag mr-1"></i>
                                  Sell
                                </button>
                              )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteListing(listing._id);
                                }}
                                className="text-white p-1.5 rounded-lg shadow-md transition-colors"
                                style={{ backgroundColor: '#222831' }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#393E46'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = '#222831'}
                                title="Delete listing"
                              >
                                <i className="fas fa-trash text-sm"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Chats Tab */}
              {activeTab === 'chats' && (
                <div>
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 md:mb-6 gap-3">
                    <h3 className="text-lg font-semibold" style={{ color: '#1A3D64' }}>
                      My Chats {chats.length > 0 && <span className="text-sm text-gray-500">({chats.length} conversations)</span>}
                    </h3>
                    <Link
                      to="/chat"
                      className="bg-indigo-600 text-white px-3 md:px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors text-sm md:text-base w-full sm:w-auto text-center"
                    >
                      <i className="fas fa-comments mr-2"></i>Open Full Chat
                    </Link>
                  </div>
                  {chats.length === 0 ? (
                    <div className="text-center py-8 md:py-12 px-4">
                      <i className="fas fa-comments text-3xl md:text-4xl text-gray-400 mb-3 md:mb-4"></i>
                      <h3 className="text-lg md:text-xl font-semibold text-gray-600 mb-2">No chats yet</h3>
                      <p className="text-sm md:text-base text-gray-500 mb-4">Start browsing items to connect with owners</p>
                      <Link
                        to="/rent-items"
                        className="bg-indigo-600 text-white px-4 md:px-6 py-2 rounded-lg hover:bg-indigo-700 transition-colors text-sm md:text-base"
                      >
                        Browse Items
                      </Link>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {chats.slice(0, 5).map(chat => {
                        // Safe access to buyer and seller with null checks
                        const buyer = chat.buyer || {};
                        const seller = chat.seller || {};
                        const otherUser = buyer._id === userStats?.userId ? seller : buyer;
                        const lastMessage = chat.messages && chat.messages.length > 0 ? chat.messages[chat.messages.length - 1] : null;

                        // Safe access to listing and images
                        const listing = chat.listing || {};
                        const listingImage = listing.images && listing.images.length > 0 ? listing.images[0] : null;
                        const listingName = listing.itemName || 'Unknown Item';

                        // Skip this chat if essential data is missing
                        if (!chat._id || !otherUser._id) {
                          return null;
                        }

                        return (
                          <Link
                            key={chat._id}
                            to="/chat"
                            className="block border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                          >
                            <div className="flex items-center space-x-4">
                              {listingImage ? (
                                <img
                                  src={getImageUrl(listingImage)}
                                  alt={listingName}
                                  className="w-16 h-16 object-contain rounded-lg bg-gray-50"
                                  onError={(e) => {
                                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjY0IiBoZWlnaHQ9IjY0IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yMCAyMEg0NFY0NEgyMFYyMFoiIHN0cm9rZT0iIzlDQTNBRiIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz4KPHA+CjwvcGF0aD4KPC9zdmc+';
                                  }}
                                />
                              ) : (
                                <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                                  <i className="fas fa-image text-gray-400 text-xl"></i>
                                </div>
                              )}
                              <div className="flex-1">
                                <h4 className="font-semibold">{listingName}</h4>
                                <p className="text-gray-600">
                                  Chat with {otherUser?.name || 'Unknown User'}
                                </p>
                                <p className="text-sm text-gray-500">
                                  {lastMessage && lastMessage.message
                                    ? `${(lastMessage.sender?._id === userStats?.userId || lastMessage.sender === userStats?.userId) ? 'You: ' : (lastMessage.sender?.name ? lastMessage.sender.name + ': ' : '')}${lastMessage.message.substring(0, 50)}${lastMessage.message.length > 50 ? '...' : ''}`
                                    : lastMessage && !lastMessage.message
                                    ? `${(lastMessage.sender?._id === userStats?.userId || lastMessage.sender === userStats?.userId) ? 'You: ' : (lastMessage.sender?.name ? lastMessage.sender.name + ': ' : '')}Image`
                                    : 'No messages yet'
                                  }
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-sm text-gray-500">
                                  {new Date(chat.updatedAt).toLocaleDateString()}
                                </p>
                                <span className="bg-indigo-100 text-indigo-800 px-2 py-1 rounded text-xs">
                                  {chat.messages ? chat.messages.length : 0} messages
                                </span>
                              </div>
                            </div>
                          </Link>
                        );
                      }).filter(Boolean)}
                      {chats.length > 5 && (
                        <div className="text-center pt-4">
                          <Link
                            to="/chat"
                            className="text-indigo-600 hover:text-indigo-700 font-medium"
                          >
                            View all {chats.length} conversations →
                          </Link>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Rented Items Tab */}
              {activeTab === 'rented' && (
                <div>
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 md:mb-6 gap-3">
                    <h3 className="text-lg font-semibold" style={{ color: '#222831' }}>
                      Rented Items ({stats.rented})
                    </h3>
                    <div className="text-sm" style={{ color: '#393E46' }}>
                      Items currently rented out to customers
                    </div>
                  </div>

                  {loading ? (
                    <div className="text-center py-6 md:py-8">
                      <SimpleLoader size="lg" text="Loading rented items..." />
                    </div>
                  ) : getFilteredListings().length === 0 ? (
                    <div className="text-center py-8 md:py-12 px-4">
                      <i className="fas fa-handshake text-3xl md:text-4xl mb-3 md:mb-4" style={{ color: '#948979' }}></i>
                      <h3 className="text-lg md:text-xl font-semibold mb-2" style={{ color: '#393E46' }}>No rented items</h3>
                      <p className="text-sm md:text-base mb-4" style={{ color: '#393E46', opacity: 0.8 }}>
                        Items you rent out will appear here with renter details
                      </p>
                      <Link
                        to="/list-items"
                        className="bg-indigo-600 text-white px-4 md:px-6 py-2 rounded-lg hover:bg-indigo-700 transition-colors text-sm md:text-base"
                      >
                        List Items to Rent
                      </Link>
                    </div>
                  ) : (
                    <div className="space-y-4 md:space-y-6">
                      {getFilteredListings().map(listing => (
                        <div key={listing._id} className="border-2 rounded-xl p-4 md:p-6 bg-white shadow-lg listing-clickable relative" style={{ borderColor: '#948979' }}>
                          {/* Action Button - Desktop: Bottom Right */}
                          <div className="absolute bottom-4 right-4 z-10 hidden md:block">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMarkAsReturned(listing._id);
                              }}
                              className="text-white px-4 py-2 rounded-lg shadow-md transition-colors font-medium"
                              style={{ backgroundColor: '#948979' }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#393E46'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = '#948979'}
                            >
                              <i className="fas fa-undo mr-2"></i>Mark as Returned
                            </button>
                          </div>

                          {/* Rented Item Content - Clickable Area */}
                          <div
                            className="cursor-pointer hover:bg-gray-50 p-3 rounded-lg transition-colors mb-4"
                            onClick={() => navigate(`/listing/${listing._id}`)}
                          >
                            {/* Rented Item Header */}
                            <div className="flex items-start space-x-4 mb-4">
                              <img
                                src={getImageUrl(listing.images[0])}
                                alt={listing.itemName}
                                className="w-20 h-20 md:w-24 md:h-24 object-contain rounded-lg flex-shrink-0 listing-image bg-gray-50"
                              />
                              <div className="flex-1">
                                <div className="flex flex-col md:flex-row md:justify-between md:items-start">
                                  <div>
                                    <h4 className="font-bold text-lg md:text-xl listing-title" style={{ color: '#222831' }}>{listing.itemName}</h4>
                                    <p className="text-sm md:text-base" style={{ color: '#393E46' }}>
                                      ₹{listing.price}{(listing.listingType || 'rent') === 'rent' ? '/day' : ''} • {listing.itemType === 'jewellery' ? (listing.jewellerySize || 'Free Size') : listing.size} • {listing.location}
                                    </p>
                                    <div className="flex flex-wrap gap-2 mt-2">
                                      <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-xs font-medium">
                                        <i className="fas fa-handshake mr-1"></i>RENTED
                                      </span>
                                      <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs capitalize">
                                        {listing.category || 'Handycraft'}
                                      </span>
                                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs capitalize">
                                        {listing.itemType === 'jewellery' ? listing.jewelleryType : (listing.itemType === 'handycraft' ? listing.handycraftType : listing.clothingType)}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="text-right mt-2 md:mt-0">
                                    <span className="text-2xl font-bold text-green-600">₹{listing.price}</span>
                                    <p className="text-xs" style={{ color: '#393E46' }}>per day</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Rental Details Card */}
                          {listing.rentalDetails && listing.rentalDetails.renterName && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                              <h5 className="font-bold text-blue-800 mb-3">
                                <i className="fas fa-user-circle mr-2"></i>
                                Rental Details:
                              </h5>
                              <div className="space-y-2 text-blue-700 text-sm">
                                <p>
                                  <strong>Renter:</strong> {listing.rentalDetails.renterName}
                                </p>
                                <p>
                                  <strong>Contact:</strong> {listing.rentalDetails.renterContact}
                                </p>
                                {listing.rentalDetails.renterLocation && (
                                  <p>
                                    <strong>Location:</strong> {listing.rentalDetails.renterLocation}
                                  </p>
                                )}
                                <p>
                                  <strong>Rented on:</strong> {new Date(listing.rentalDetails.rentedAt).toLocaleDateString()}
                                </p>
                                <p>
                                  <strong>Expected return:</strong> {new Date(listing.rentalDetails.expectedReturnDate).toLocaleDateString()}
                                </p>
                                {listing.rentalDetails.notes && (
                                  <p>
                                    <strong>Notes:</strong> {listing.rentalDetails.notes}
                                  </p>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Sold Items Tab */}
              {activeTab === 'sold' && (
                <div>
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 md:mb-6 gap-3">
                    <h3 className="text-lg font-semibold" style={{ color: '#222831' }}>
                      Sold Items ({stats.sold})
                    </h3>
                    <div className="text-sm" style={{ color: '#393E46' }}>
                      Items you have successfully sold
                    </div>
                  </div>

                  {loading ? (
                    <div className="text-center py-6 md:py-8">
                      <SimpleLoader size="lg" text="Loading sold items..." />
                    </div>
                  ) : getFilteredListings().length === 0 ? (
                    <div className="text-center py-8 md:py-12 px-4">
                      <i className="fas fa-tag text-3xl md:text-4xl mb-3 md:mb-4" style={{ color: '#948979' }}></i>
                      <h3 className="text-lg md:text-xl font-semibold mb-2" style={{ color: '#393E46' }}>No sold items</h3>
                      <p className="text-sm md:text-base mb-4" style={{ color: '#393E46', opacity: 0.8 }}>
                        Items you sell will appear here with buyer details
                      </p>
                      <Link
                        to="/list-items"
                        className="bg-indigo-600 text-white px-4 md:px-6 py-2 rounded-lg hover:bg-indigo-700 transition-colors text-sm md:text-base"
                      >
                        List Items to Sell
                      </Link>
                    </div>
                  ) : (
                    <div className="space-y-4 md:space-y-6">
                      {getFilteredListings().map(listing => (
                        <div key={listing._id} className="border-2 rounded-xl p-4 md:p-6 bg-white shadow-lg listing-clickable relative" style={{ borderColor: '#948979' }}>
                          {/* Sold Item Content - Clickable Area */}
                          <div
                            className="cursor-pointer hover:bg-gray-50 p-3 rounded-lg transition-colors mb-4"
                            onClick={() => navigate(`/listing/${listing._id}`)}
                          >
                            {/* Sold Item Header */}
                            <div className="flex items-start space-x-4 mb-4">
                              <img
                                src={getImageUrl(listing.images[0])}
                                alt={listing.itemName}
                                className="w-20 h-20 md:w-24 md:h-24 object-contain rounded-lg flex-shrink-0 listing-image bg-gray-50"
                              />
                              <div className="flex-1">
                                <div className="flex flex-col md:flex-row md:justify-between md:items-start">
                                  <div>
                                    <h4 className="font-bold text-lg md:text-xl listing-title" style={{ color: '#222831' }}>{listing.itemName}</h4>
                                    <p className="text-sm md:text-base" style={{ color: '#393E46' }}>
                                      ₹{listing.price} • {listing.itemType === 'jewellery' ? (listing.jewellerySize || 'Free Size') : listing.size} • {listing.location}
                                    </p>
                                    <div className="flex flex-wrap gap-2 mt-2">
                                      <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-xs font-medium">
                                        <i className="fas fa-tag mr-1"></i>SOLD
                                      </span>
                                      <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs capitalize">
                                        {listing.category || 'Handycraft'}
                                      </span>
                                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs capitalize">
                                        {listing.itemType === 'jewellery' ? listing.jewelleryType : (listing.itemType === 'handycraft' ? listing.handycraftType : listing.clothingType)}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="text-right mt-2 md:mt-0">
                                    <span className="text-2xl font-bold text-green-600">₹{listing.saleDetails?.salePrice || listing.price}</span>
                                    <p className="text-xs" style={{ color: '#393E46' }}>sale price</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Sale Details Card */}
                          {listing.saleDetails && listing.saleDetails.buyerName && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                              <h5 className="font-bold text-green-800 mb-3">
                                <i className="fas fa-user-circle mr-2"></i>
                                Sale Details:
                              </h5>
                              <div className="space-y-2 text-green-700 text-sm">
                                <p>
                                  <strong>Buyer:</strong> {listing.saleDetails.buyerName}
                                </p>
                                <p>
                                  <strong>Contact:</strong> {listing.saleDetails.buyerContact}
                                </p>
                                {listing.saleDetails.buyerLocation && (
                                  <p>
                                    <strong>Location:</strong> {listing.saleDetails.buyerLocation}
                                  </p>
                                )}
                                <p>
                                  <strong>Sold on:</strong> {new Date(listing.saleDetails.soldAt).toLocaleDateString()}
                                </p>
                                <p>
                                  <strong>Sale Price:</strong> ₹{listing.saleDetails.salePrice || listing.price}
                                </p>
                                {listing.saleDetails.notes && (
                                  <p>
                                    <strong>Notes:</strong> {listing.saleDetails.notes}
                                  </p>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

            </div>
          </div>
        </div>

        {/* Rental Modal */}
        {showRentalModal && selectedListing && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Mark as Rented</h3>
                <button
                  onClick={() => setShowRentalModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <i className="fas fa-times text-xl"></i>
                </button>
              </div>

              <div className="mb-4">
                <h4 className="font-medium text-gray-800">{selectedListing.itemName}</h4>
                <p className="text-sm text-gray-600">
                  ₹{selectedListing.price}{(selectedListing.listingType || 'rent') === 'rent' ? '/day' : ''} • {selectedListing.itemType === 'jewellery' ? (selectedListing.jewellerySize || 'Free Size') : selectedListing.size}
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Renter's Name *
                  </label>
                  <input
                    type="text"
                    value={rentalForm.renterName}
                    onChange={(e) => setRentalForm({ ...rentalForm, renterName: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter renter's name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Number *
                  </label>
                  <input
                    type="tel"
                    value={rentalForm.renterContact}
                    onChange={(e) => setRentalForm({ ...rentalForm, renterContact: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="10-digit mobile number"
                    maxLength="10"
                  />
                  <p className="text-xs text-gray-500 mt-1">Enter exactly 10 digits</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Renter's Location (optional)
                  </label>
                  <input
                    type="text"
                    value={rentalForm.renterLocation}
                    onChange={(e) => setRentalForm({ ...rentalForm, renterLocation: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter renter's location"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Expected Return Date *
                  </label>
                  <input
                    type="date"
                    value={rentalForm.expectedReturnDate}
                    onChange={(e) => setRentalForm({ ...rentalForm, expectedReturnDate: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    min={new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                    max={new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                  />
                  <p className="text-xs text-gray-500 mt-1">Must be between tomorrow and 30 days from today</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes (optional)
                  </label>
                  <textarea
                    value={rentalForm.notes}
                    onChange={(e) => setRentalForm({ ...rentalForm, notes: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Any additional notes about the rental"
                    rows="2"
                  />
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => setShowRentalModal(false)}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={submitRental}
                  className="flex-1 bg-orange-600 text-white py-2 rounded-md hover:bg-orange-700 transition-colors"
                >
                  Mark as Rented
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Sale Modal */}
        {showSaleModal && selectedListing && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Mark as Sold</h3>
                <button
                  onClick={() => setShowSaleModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <i className="fas fa-times text-xl"></i>
                </button>
              </div>

              <div className="mb-4">
                <h4 className="font-medium text-gray-800">{selectedListing.itemName}</h4>
                <p className="text-sm text-gray-600">
                  ₹{selectedListing.price} • {selectedListing.itemType === 'jewellery' ? (selectedListing.jewellerySize || 'Free Size') : selectedListing.size}
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Buyer's Name *
                  </label>
                  <input
                    type="text"
                    value={saleForm.buyerName}
                    onChange={(e) => setSaleForm({ ...saleForm, buyerName: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter buyer's name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Number *
                  </label>
                  <input
                    type="tel"
                    value={saleForm.buyerContact}
                    onChange={(e) => setSaleForm({ ...saleForm, buyerContact: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="10-digit mobile number"
                    maxLength="10"
                  />
                  <p className="text-xs text-gray-500 mt-1">Enter exactly 10 digits</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Buyer's Location (optional)
                  </label>
                  <input
                    type="text"
                    value={saleForm.buyerLocation}
                    onChange={(e) => setSaleForm({ ...saleForm, buyerLocation: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter buyer's location"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes (optional)
                  </label>
                  <textarea
                    value={saleForm.notes}
                    onChange={(e) => setSaleForm({ ...saleForm, notes: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Any additional notes about the sale"
                    rows="2"
                  />
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  onClick={() => setShowSaleModal(false)}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={submitSale}
                  className="flex-1 bg-green-600 text-white py-2 rounded-md hover:bg-green-700 transition-colors"
                >
                  Mark as Sold
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Return Confirmation Modal */}
        {showReturnConfirm && itemToReturn && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Confirm Return</h3>
                <button
                  onClick={() => setShowReturnConfirm(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <i className="fas fa-times text-xl"></i>
                </button>
              </div>

              <div className="mb-4">
                <h4 className="font-medium text-gray-800">{itemToReturn.itemName}</h4>
                <p className="text-sm text-gray-600">Currently rented</p>

                {/* Show rental details in return modal */}
                {itemToReturn.rentalDetails && (
                  <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded text-sm">
                    <div className="font-medium text-blue-800 mb-2">Rental Details:</div>
                    <div className="text-blue-700 space-y-1">
                      <div><strong>Renter:</strong> {itemToReturn.rentalDetails.renterName}</div>
                      <div><strong>Contact:</strong> {itemToReturn.rentalDetails.renterContact}</div>
                      {itemToReturn.rentalDetails.renterLocation && (
                        <div><strong>Location:</strong> {itemToReturn.rentalDetails.renterLocation}</div>
                      )}
                      <div><strong>Rented on:</strong> {new Date(itemToReturn.rentalDetails.rentedAt).toLocaleDateString()}</div>
                      <div><strong>Expected return:</strong> {new Date(itemToReturn.rentalDetails.expectedReturnDate).toLocaleDateString()}</div>
                      {itemToReturn.rentalDetails.notes && (
                        <div><strong>Notes:</strong> {itemToReturn.rentalDetails.notes}</div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Return Notes (optional)
                </label>
                <textarea
                  value={returnNotes}
                  onChange={(e) => setReturnNotes(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Any notes about the item condition upon return"
                  rows="3"
                />
              </div>

              <p className="text-gray-700 mb-6">
                Are you sure you want to mark this item as returned and make it available for rent again?
              </p>

              <div className="flex space-x-3">
                <button
                  onClick={() => setShowReturnConfirm(false)}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmReturn}
                  className="flex-1 bg-green-600 text-white py-2 rounded-md hover:bg-green-700 transition-colors"
                >
                  Mark as Returned
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && itemToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-red-600">Delete Listing</h3>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <i className="fas fa-times text-xl"></i>
                </button>
              </div>

              <div className="mb-4">
                <h4 className="font-medium text-gray-800">{itemToDelete.itemName}</h4>
                <p className="text-sm text-gray-600">
                  ₹{itemToDelete.price}/day • {itemToDelete.itemType === 'jewellery' ? (itemToDelete.jewellerySize || 'Free Size') : itemToDelete.size}
                </p>
              </div>

              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start">
                  <i className="fas fa-exclamation-triangle text-red-500 mt-1 mr-3"></i>
                  <div>
                    <h5 className="font-medium text-red-800 mb-1">Warning: This action cannot be undone</h5>
                    <p className="text-sm text-red-700">
                      Deleting this listing will permanently remove:
                    </p>
                    <ul className="text-sm text-red-700 mt-2 ml-4 list-disc">
                      <li>The listing and all its details</li>
                      <li>All uploaded images</li>
                      <li>All associated data for this item</li>
                    </ul>
                  </div>
                </div>
              </div>

              <p className="text-gray-700 mb-6">
                Are you sure you want to permanently delete this listing?
              </p>

              <div className="flex space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 bg-red-600 text-white py-2 rounded-md hover:bg-red-700 transition-colors"
                >
                  <i className="fas fa-trash mr-2"></i>
                  Delete Permanently
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Listing Modal */}
        {showEditModal && itemToEdit && (
          <EditListing
            listing={itemToEdit}
            onClose={handleEditClose}
            onUpdate={handleEditUpdate}
          />
        )}
        <Footer />
      </div>
    </>
  );
};

export default Dashboard;